import { useEffect, useCallback } from "react";
import { HubConnection } from "@microsoft/signalr";
import { CrosswordSessionUpdateDto } from "../models/crossword-session-update-dto";
import { GridCellState } from "../GridCellState";
import logger from "../utils/logger";
import { AmendedGridStateFromUpdate } from "../utils/GridUtils";
import { fadeTime } from "../components/CrosswordCell";

interface UseReceiveGridUpdateProps {
  connection: HubConnection | null;
  sessionId: number;
  userKey: string;
  setGridState: React.Dispatch<React.SetStateAction<GridCellState[][]>>;
  setScratchpad: React.Dispatch<React.SetStateAction<string>>;
  setUpdatedCells: React.Dispatch<React.SetStateAction<{ row: number; col: number }[]>>;
  setUpdatingUsers: React.Dispatch<React.SetStateAction<Record<string, number>>>; // Changed type
  setNumUsers: React.Dispatch<React.SetStateAction<number>>;
}

const useReceiveGridUpdate = ({
  connection,
  sessionId,
  userKey,
  setGridState,
  setScratchpad,
  setUpdatedCells,
  setUpdatingUsers,
  setNumUsers,
}: UseReceiveGridUpdateProps): void => {
  const handleGridUpdate = useCallback(
    (update: CrosswordSessionUpdateDto) => {
      if (update.sessionId === sessionId) {
        setGridState((prevGrid) => AmendedGridStateFromUpdate(prevGrid, update.gridState));
        const amendingUserKey = update.gridState.substring(
          2 * (15 * update.rowChanged + update.colChanged) + 1,
          2 * (15 * update.rowChanged + update.colChanged) + 2
        );
        if (amendingUserKey !== userKey) {
          logger.debug(
            `User ${amendingUserKey} updated cell ${update.rowChanged},${update.colChanged}`
          );
          setUpdatedCells((prevCells) => [
            ...prevCells,
            { row: update.rowChanged, col: update.colChanged },
          ]);
          
          // Set/update expiry time for this user
          setUpdatingUsers((prevUsers) => ({
            ...prevUsers,
            [amendingUserKey]: Date.now() + fadeTime // Store expiry time
          }));

          setNumUsers(update.userCount);
          setTimeout(() => {
            // Remove this specific cell
            setUpdatedCells((prevCells) =>
              prevCells.filter(
                (cell) => !(cell.row === update.rowChanged && cell.col === update.colChanged)
              )
            );
            
            // Check for any expired users
            const now = Date.now();
            setUpdatingUsers((prevUsers) => {
              const updatedUsers = { ...prevUsers };
              
              // Check all users including this one
              Object.keys(updatedUsers).forEach(key => {
                if (updatedUsers[key] <= now) {
                  delete updatedUsers[key];
                }
              });
              
              return updatedUsers;
            });
          }, fadeTime + 100);
        }
      }
    },
    [sessionId, setGridState, setUpdatedCells, setUpdatingUsers, userKey,setNumUsers]
  );

  useEffect(() => {
    if (connection && sessionId) {
      connection.on("ReceiveGridUpdate", handleGridUpdate);
      connection.on("ReceiveScratchpad", (update: CrosswordSessionUpdateDto) => {
        if (update.sessionId === sessionId) {
          setScratchpad(update.scratchpad);
        }
      });
    } else {
      logger.warn("No connection or session id");
    }
    return () => {
      if (connection) {
        connection.off("ReceiveGridUpdate");
        connection.off("ReceiveScratchpad");
      }
    };
  }, [connection, sessionId, handleGridUpdate, setScratchpad]);
};

export default useReceiveGridUpdate;
